import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

import { MenuItem } from 'primeng/api'
import { Menu, MenuModule } from 'primeng/menu'

import { FlxButtonModule, FlxIconModule } from '@flowx/angular-ui-toolkit'

import { getElementStateConfig } from './output.states'

import { ExpressionCondition } from '../../constants/expressions.constants'
import { OUTPUT_ARROW } from '../../constants/icons.constants'
import { ScriptingIconsModule } from '../../scripting-icons.module'
import { CodeEditorComponent } from '../code-editor/code-editor.component'

export type OutcomeError = { msg: string }

// TODO: move info icon into global icons module and skip importing the ai icons module
@Component({
  selector: 'flx-scripting-output',
  imports: [
    CommonModule,
    FormsModule,
    ScriptingIconsModule,
    CodeEditorComponent,
    MenuModule,
    FlxIconModule,
    FlxButtonModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './scripting-output.component.html',
})
export class ScriptingOutputComponent {
  #domSanitizer = inject(DomSanitizer)

  codeEditorOptions = {
    language: 'json',
  }

  outputArrowSVG: SafeHtml = this.#domSanitizer.bypassSecurityTrustHtml(OUTPUT_ARROW)

  condition = input<ExpressionCondition>(null)
  result = input.required()
  valueIsComputed = input.required()
  error = input<OutcomeError>(null)
  showOutcome = input<boolean>(false)
  showVisualOutcome = computed(
    () => this.valueIsComputed() && this.showOutcome() && this.resultState()
  )
  fixError = output<string>()
  aiActionsMenu = viewChild<Menu>('aiActionsMenu')
  aiActionsButton = viewChild<ElementRef>('aiActionsButton')
  showAiActions = input<boolean>(false)

  resultString = computed(() => {
    if (this.error()?.msg) {
      return this.error().msg.toString()
    }

    if (!this.valueIsComputed()) {
      return ''
    }

    if (this.result() === undefined) {
      return 'undefined'
    }

    try {
      return JSON.stringify(this.result(), undefined, 2)
    } catch {
      return ''
    }
  })

  aiActionMenuOptions = computed<MenuItem[]>((): MenuItem[] => {
    if (!this.showAiActions()) {
      return []
    }

    const iconStyles = 'flex justify-center items-center w-5 h-5 text-yellow-500'
    const fixErrorsOption: MenuItem = {
      label: 'Fix Errors',
      icon: `${iconStyles} ph ph-warning`,
      command: (): void => {
        this.fixError.emit(this.error().msg)
      },
    }
    return [fixErrorsOption]
  })

  hasError = computed(() => !!this.error()?.msg)
  typeWarning = computed(() => typeof this.result() !== 'boolean')
  showResultTypeInfo = computed(
    () => this.showOutcome() && this.typeWarning() && this.valueIsComputed() && this.resultState()
  )

  resultState = computed(() =>
    this.result() !== null ? getElementStateConfig(this.condition(), !!this.result()) : null
  )
  footerCodeEditorWarnings = signal({ show: false, expanded: false })

  onFixErrorClick(): void {
    if (this.hasError()) {
      this.fixError.emit(this.error().msg)
    }
  }
}
